import { FREQUENCY_BUCKETS, type Dictionary } from "@kensio/pinyinjs";

/**
 * How common a word is in general, as opposed to in the text on the page.
 *
 * The segmenter orders by two different things and a reader has to be able to
 * tell them apart. One is how often this text uses a word, which the text
 * decides. The other is how often anybody uses it, which is what this answers.
 * A text about banking uses 银行 twenty times and 的 five, and only one of
 * those two is a common word.
 *
 * The number comes from the corpus behind `@kensio/pinyinjs`, quantised to
 * sixteen levels and carried inside every dictionary tier. Nothing extra is
 * fetched to read it.
 */

/**
 * The bucket a word's frequency falls in, from 0 (rarest) to 15 (commonest).
 *
 * **Found by binary search over the dictionary's own keys**, which is more
 * awkward than it should be. `Dictionary.frequencyAt` takes a position and
 * `Dictionary.wordAt` hands one back, but nothing exposes the key index that
 * already sits inside the dictionary and answers this in one call. See
 * KensioSoftware/pinyinjs#169, which asks for `frequencyOf`. When that lands
 * this becomes a delegation and the search goes.
 *
 * The alternative was arithmetic on `WordEntry.cost`, which is
 * `15 - bucket + WORD_CHARGE`. `WORD_CHARGE` is not exported, so that means a
 * hard-coded 4.62 in this file and a wrong answer the day the package retunes
 * it. A search over published methods cannot go quietly wrong.
 *
 * Keys sort by UTF-16 code unit, which is what `<` compares strings by, so the
 * search and the index agree by construction.
 */
export function frequencyBucketOf(
  dictionary: Dictionary,
  word: string,
): number | undefined {
  let low = 0;
  let high = dictionary.size - 1;

  while (low <= high) {
    const middle = (low + high) >> 1;
    const key = dictionary.wordAt(middle);

    if (key === word) {
      return dictionary.frequencyAt(middle);
    }

    if (key < word) {
      low = middle + 1;
    } else {
      high = middle - 1;
    }
  }

  return undefined;
}

/** What a band is called, and where it starts on the sixteen-level scale. */
export interface FrequencyBand {
  /** What the column says. */
  readonly name: string;

  /** The lowest bucket this band covers. */
  readonly from: number;
}

/**
 * The five bands, commonest first.
 *
 * **Sixteen levels is more resolution than the page can use and less than it
 * looks.** Measured over the 8,828 HSK words the standard tier holds, the
 * buckets in use run 4 to 15 and two thirds of that vocabulary sits in three of
 * them. Printing a number would offer a precision the scale does not have, and
 * over the 264 distinct words in 1,000 characters of running text the sixteen
 * levels resolve to eleven with 71 words sharing one.
 *
 * The cuts are a judgement over that distribution rather than an even split.
 * 是 lands at 15 and 的 and 我 at 14, which is the grammatical core and is its
 * own band. 学习, 喜欢 and 银行 all sit at 10. 嘲笑 is 7, 推诿 5 and 拗口 4.
 *
 * Sorting uses the bucket and never these bands, so a reader ordering the list
 * by this column gets the full sixteen levels and sees five names.
 */
export const FREQUENCY_BANDS: readonly FrequencyBand[] = [
  { name: "very common", from: 13 },
  { name: "common", from: 10 },
  { name: "fairly common", from: 7 },
  { name: "uncommon", from: 4 },
  { name: "rare", from: 0 },
];

/**
 * What a bucket is called.
 *
 * A word the dictionary has no entry for has no bucket and no band. That is a
 * character or a run the tier does not hold, and saying "rare" about it would
 * be claiming a measurement nobody made.
 */
export function frequencyBandOf(
  bucket: number | undefined,
): FrequencyBand | undefined {
  if (bucket === undefined || bucket < 0 || bucket >= FREQUENCY_BUCKETS) {
    return undefined;
  }

  return FREQUENCY_BANDS.find((band) => bucket >= band.from);
}
